import { Component, Input, ViewChild, ElementRef, HostListener, OnDestroy, OnChanges, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ReplaySubject } from 'rxjs';
import { ThemeService } from '@wind-log_library/fe-component-library';
import { takeUntil } from 'rxjs/operators';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { NoUnderscorePipe } from 'app/shared/pipes/no-underscore/no-underscore.pipe';
import * as d3 from 'd3';
import d3Tip from "d3-tip";
import moment from 'moment';
import { DateTimeFormatPreferencePipe } from 'app/shared/pipes/date-time-format-preference/date-time-format-preference.pipe';
import { UserPreferencesUnitSettingsService } from 'app/shared/services/user-preferences-unit-settings/user-preferences-unit-settings.service';

@Component({
    selector: 'app-availability-heatmap',
    templateUrl: './availability-heatmap.component.html',
    styleUrls: ['./availability-heatmap.component.scss'],
    standalone: false
})
export class AvailabilityHeatmapComponent implements OnInit, OnChanges, OnDestroy {

	@Input() level: string;
	@Input() daysCount: number;
	@Input() from;
	@Input() availabilities: any[] = [];
	@Input() chartType: ChartType = ChartType.Percentage;
	@Input() timeStamps: number[];

	@ViewChild('container', { static: true }) container: ElementRef;

	public turbineHovered: any;
	public showSerialPopup = false;
	public popupLeft = 0;
	public popupTop = 0;
	public clickable = false;
	public chartId = Math.random().toString(36).substring(2);
	public loading;
	public chartTypeString: string = 'Rated power';

	private margin = { top: 10, right: 0, bottom: 30, left: 60 };
	private subSize;
	private size = new ReplaySubject();
	private chartCols: any;
	private rowHeight = 19;
	private unsubscriber = new ReplaySubject();

	constructor(
		private element: ElementRef,
		private noUnderscore: NoUnderscorePipe,
		private router: Router,
		private themeService: ThemeService,
		private translateService: TranslateService,
		private dateTimeFormatPreferencePipe: DateTimeFormatPreferencePipe,
		private userPreferencesUnitSettingsService: UserPreferencesUnitSettingsService
	) { }

	ngOnInit() {
		moment.locale(this.translateService.currentLang);
		this.translateService.onLangChange.pipe(takeUntil(this.unsubscriber)).subscribe((event: LangChangeEvent) => {
			this.loading = true;
			if (event) {
				moment.locale(this.translateService.currentLang);
				this.reset();
				this.loading = false;
				this.getData();
			}
		});
	}

	ngOnChanges() {
		this.loading = true;
		if (this.availabilities) {
			this.reset();
			this.loading = false;
			this.getData();
		}
	}


	@HostListener('window:resize')
	onResize() {
		this.size.next({
			width: this.container.nativeElement.offsetWidth,
			height: 19 * (this.availabilities ? this.availabilities.length : 2) + 20
		});
	}

	getData() {
		this.sort(this.availabilities);
		this.size.subscribe((size: any) => {
			this.reset();
			this.drawChart();
		});

		this.onResize();
	}

	chartClick(e) {
		if (this.clickable && this.level === 'portfolio') {
			const chartHeight = this.rowHeight * this.chartCols.length;
			const availability = Math.floor((e.offsetY + this.margin.top) / (((chartHeight) / (this.availabilities.length)))) - 1
			const park = availability >= 0 ? this.availabilities[availability].row.name : null;

			if (park) this.router.navigate(['/forecast/park/' + park + '/overview']);
		}
	}

	private formatData = () => {
		const finalData = this.availabilities.map((data) => data.availability.map((value, index) => ({
			group: index,
			variable: this.noUnderscore.transform(data.row.code),
			value,
			valueString: this.getStringForItem(value),
			name: data.row.displayName || data.row.name,
			date: this.dateTimeFormatPreferencePipe.transform(moment(this.timeStamps[index]).toDate(), 'dmt'),
			level: this.level === 'portfolio' ? 'Park' : 'Turbine',
		}))).flat();
		return finalData;
	};

	private getChartLabels = (from: any, step: any, range: any): string[] => {
		const xAxisLabels = [from];

		for (let x = 1; x < this.availabilities[0].availability.length; x += 1) {
			xAxisLabels.push(from + (step * x));
		}
		return xAxisLabels;
	}

	private 

	drawChart() {
		if (this.availabilities.length === 0) {
			return;
		}
		const step = this.daysCount > 1 ? 3600000 : 600000;
		let chartTypeString: string = this.chartType === 1 ? 'Rated power' : 'Status';
		
		// Tooltip information
		const tip = d3Tip()
		tip
			.attr('data-html2canvas-ignore',true)
			.attr('class', 'd3-tip')
			.style('z-index', '9999')
			.offset([-10, 0])
			.html(function (d) {
				const tooltip = `<span style="color:var(--primary); font-size: 16px; font-weight: 600;">${d.date}</span><br>
								 <span>${d.level}: </span> <strong>${d.name}</strong><br>
								 <span>${chartTypeString}: </span><strong>${d.valueString}</strong>`
				return tooltip
			})
		
		const tipNav = d3Tip()
		tipNav
			.attr('data-html2canvas-ignore',true)
			.attr('class', 'd3-tip')
			.style('z-index', '9999')
			.offset([-10, 0])
			.html(function (d) {
				const info = getTicksToolTipInfo(d); 
				const tooltip = `<span>${info.level}: </span> <strong>${info.tick}</strong><br>`
				return tooltip
			})

		const getTicksToolTipInfo = (code) => {
			const tick = this.level === 'portfolio' ? this.availabilities.find(el => el.row.code === code).row.displayName
				: this.availabilities.find(el => this.noUnderscore.transform(el.row.code) === code).row.serialNumeric;
			const level = this.level === 'portfolio' ? 'Park' : 'Serial Number';
			return { tick, level }
		}
		// Data formatted
		const formattedData = this.formatData();

		// Labels of row and columns
		const myDates = this.getChartLabels(this.from, step, this.daysCount * 24);
		const myGroups = Array.from({ length: this.availabilities[0].availability.length + 1 }, (_, index) => (index).toString());
		this.chartCols = this.availabilities.map((value) => this.noUnderscore.transform(value.row.code));

		// Setting the dimensions and margins of the graph
		const width = this.element.nativeElement.offsetWidth - this.margin.left - this.margin.right,
			height = this.rowHeight * this.chartCols.length;

		// append the svg object to the body of the page
		const svg = d3.select(this.container.nativeElement)
			.append("svg")
			.attr("width", width + this.margin.left + this.margin.right)
			.attr("height", height + this.margin.top + this.margin.bottom)
			.append("g")
			.attr("transform", `translate(${this.margin.left},${this.margin.top})`);


		svg.call(tip);
		// Three function that change the tooltip when user hover / move / leave a cell
		const mouseover = (d) => {
			const cont = document.getElementById(`my_dataviz-${this.chartId}`);
			this.clickable = d3.mouse(cont)[1] > this.margin.top
				&& d3.mouse(cont)[1] < (cont.offsetHeight - this.margin.bottom)
				&& d3.mouse(cont)[0] < (this.margin.left) && this.level === 'portfolio'
		}
		const mouseleave = (d) => {
			this.clickable = false;
		}

		// Build X scales and axis:
		const xAxis = d3.scaleBand()
			.range([0, width])
			.domain(myGroups)
			.padding(0.1)

		// Separate date Axis to make it independent from heatmap cells.
		const dateAxis = d3.scaleBand()
			.range([0, width])
			.domain(myDates)
		svg.append("g")
			.attr("class", `dateAxis-${this.chartId}`)
			.attr("transform", `translate(0, ${height + 8})`)
			.attr("color", this.themeService.getColor('--font'))
			.call(d3.axisBottom(dateAxis).tickFormat((d) => {
				const date = moment(d);
				if (date.minute() === 0) {
					if (this.daysCount === 7) {
						if (date.hour() === 0) {
							return this.dateTimeFormatPreferencePipe.transform(date.toDate(), 'dm');
						} else if (date.hour() === 12) {
							return this.dateTimeFormatPreferencePipe.transform(date.toDate(), 't');
						}
					} else {
						if (this.container.nativeElement.offsetWidth < 900 && this.userPreferencesUnitSettingsService.getTimeFormat().slice(0, 2) === 'hh') {
							if (date.hour() % 4 === 0) {
								return this.dateTimeFormatPreferencePipe.transform(date.toDate(), 't');
							}
						} else if (date.hour() % 2 === 0) {
							return this.dateTimeFormatPreferencePipe.transform(date.toDate(), 't');
						}
					}
				}
			}).tickSize(0))
			.select(".domain").remove();

		// Build Y scales and axis:
		const yAxis = d3.scaleBand()
			.range([0, height])
			.domain(this.chartCols)
			.padding(0.1)
		//Background yAxis
		this.chartCols.forEach((col) => {
			svg.append("line")
				.attr("class", `yAxisRect-${this.chartId}`)
				.attr("x1", -60)
				.attr("x2", -1)
				.attr("y1", this.margin.top + yAxis(col) - 1.5)
				.attr("y2", this.margin.top + yAxis(col) - 1.5)
				.attr("stroke", this.themeService.getColor('--primary-background'))
				.attr("stroke-width", height / this.chartCols.length - 1)
		})
		svg.append("g")
			.attr("class", `yAxis-${this.chartId}`)
			.attr("transform", `translate(-20, 0)`)
			.attr("color", this.themeService.getColor('--white'))
			.call(d3.axisLeft(yAxis))
			.on("mouseover", mouseover)
			.on("mosemove", mouseover)
			.on("mouseleave", mouseleave)
			.select(".domain").remove();
		svg.selectAll("g").selectAll("line").remove();
		// Build heatmap
		svg.selectAll()
			.data(formattedData, (d) => d.group + ':' + d.variable)
			.join("rect")
			.attr("x", (d) => xAxis(d.group))
			.attr("y", (d) => yAxis(d.variable))
			.attr("width", xAxis.bandwidth())
			.attr("height", yAxis.bandwidth())
			.on("mouseover", function (d) { tip.show(d, this) })
			.on("mouseout", function (d) { tip.hide(d, this) })
			.style("fill", (d) => { return this.getColorForItem(d.value) })

		svg.selectAll(".yAxis-" + this.chartId + " .tick")
			.each(function (d) {
				d3.select(this)
					.call(tipNav)
					.on("mouseover", function () {
						tipNav.show(d, this);
					})
					.on("mouseout", function () {
						tipNav.hide();
					})
					.on("click", function() {
						tipNav.hide();
					});
			});

		// Format axis labels
		d3.selectAll(`.dateAxis-${this.chartId}>.tick>text`)
			.each(function () {
				d3.select(this).style("font-size", 12 + "px");
			});
		d3.selectAll(`.yAxis-${this.chartId}>.tick>text`)
			.each(function () {
				d3.select(this).style("font-size", 12 + "px");
				d3.select(this).style("text-anchor", "middle");
			});
	}

	sort(arr) {
		const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
		arr.sort((a, b) => {
			if (collator.compare(a.row.code, b.row.code) > 0) {
				return 1;
			}
			if (collator.compare(a.row.code, b.row.code) < 0) {
				return -1;
			}
			return 0;
		});
	}

	reset() {
		d3.select(this.container.nativeElement).selectAll('svg').remove();
	}

	private getColorForItem(item: number): string {
		if (this.chartType === ChartType.Percentage) {

			if (item < 0.2) {
				return this.themeService.getColor('--chart-availability-percentage-1');
			} else if (item < 0.4) {
				return this.themeService.getColor('--chart-availability-percentage-2');
			} else if (item < 0.6) {
				return this.themeService.getColor('--chart-availability-percentage-3');
			} else if (item < 0.8) {
				return this.themeService.getColor('--chart-availability-percentage-4');
			} else if (item < 1) {
				return this.themeService.getColor('--chart-availability-percentage-5');
			} else {
				return this.themeService.getColor('--chart-availability-percentage-6');
			}
		} else if (this.chartType === ChartType.Status) {
			if (item === 0) {
				return this.themeService.getColor('--chart-availability-status-1');
			} else if (item === 1) {
				return this.themeService.getColor('--chart-availability-status-2');
			} else if (item === 2) {
				return this.themeService.getColor('--chart-availability-status-3');
			}
		}
	}

	private getStringForItem(item: number): string {
		if (this.chartType === ChartType.Percentage) {
			if (item < 0.2) {
				return '0%';
			} else if (item < 0.4) {
				return '20%';
			} else if (item < 0.6) {
				return '40%';
			} else if (item < 0.8) {
				return '60%'
			} else if (item < 1) {
				return '80%';
			} else {
				return '100%';
			}
		} else if (this.chartType === ChartType.Status) {
			if (item === 0) {
				return 'Available';
			} else if (item === 1) {
				return 'Power Limitation';
			} else if (item === 2) {
				return 'In service';
			}
		}

	}

	private subscribeOnThemeChanges(): void {
		this.themeService.getTheme().pipe(
			takeUntil(this.unsubscriber),
		).subscribe(() => {
			this.drawChart();
		});
	}

	ngOnDestroy() {
		this.reset();
		if (this.subSize) { this.subSize.unsubscribe(); }
		this.unsubscriber.next(true);
		this.unsubscriber.complete();
		this.onResize();
	}
}


export enum ChartType {
	Percentage = 1,
	Status = 2
}
